import React, { createContext, useEffect, useState } from 'react';
import { AdminToken, AuthToken, UserToken } from 'types';
import type { AdminOrUser } from 'types/interface';

import { get, getAuthTokenData } from '../lib/api';
import { appInsights } from '..';

type ContextProps = {
   user: AdminOrUser | null;
   handleUserLogin: () => Promise<void>;
   handleUserLogout: () => void;
};

export const UserContext = createContext<ContextProps>({
   user: null,
   handleUserLogin: async () => {},
   handleUserLogout: () => {},
});

export function isAdminToken(token: AuthToken | null): token is AdminToken {
   return token?.type === 'admin';
}

export function isSuperAdmin(token: AuthToken | null): token is AdminToken {
   return isAdminToken(token) && token.accessLevel === 'super-admin';
}

export function isUserToken(token: AuthToken | null): token is UserToken {
   return token?.type === 'user';
}

export function isFundManager(token: AuthToken | null): token is UserToken {
   return isUserToken(token) && Boolean(token?.managedFunds?.length);
}

const UserContextProvider: React.FC<
   React.PropsWithChildren<React.PropsWithChildren<{}>>
> = ({ children }) => {
   const [user, setUser] = useState<AdminOrUser | null>(null);
   const urlParams = new URLSearchParams(location?.search);
   const impersonationId =
      urlParams.get('impersonationId') ??
      sessionStorage.getItem('impersonationId');

   const handleUserLogin = async () => {
      const res = await get('me');
      setUser(res);

      isUserToken(res)
         ? appInsights?.setAuthenticatedUserContext(
              `${impersonationId ? 'impersonated-' : ''}${res.userId}`,
              '',
              true,
           )
         : null;
   };

   const handleUserLogout = () => {
      setUser(null);
      appInsights?.clearAuthenticatedUserContext();
   };

   useEffect(() => {
      if (impersonationId && !sessionStorage.getItem('impersonationId')) {
         sessionStorage.setItem('impersonationId', impersonationId);
      }
   }, [impersonationId]);

   const authToken = getAuthTokenData();
   const pendingUserData =
      authToken && (isUserToken(authToken) || isAdminToken(authToken)) && !user;
   useEffect(() => {
      if (pendingUserData) {
         handleUserLogin();
      }
   }, [authToken]);

   return (
      <UserContext.Provider
         value={{
            user,
            handleUserLogin,
            handleUserLogout,
         }}
      >
         {!pendingUserData && children}
      </UserContext.Provider>
   );
};

export default UserContextProvider;
